import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { interval, Subject, Subscription, takeUntil } from 'rxjs';
import { assetsConst } from 'src/app/core/const/assets.const';
import { SpinnerService } from 'src/app/core/services/spinner.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { logoutResponse } from 'src/app/models/logoutResponse';
import { responseStatus } from 'src/app/core/const/responseStatus.const';
import { environment } from 'src/environments/environment';
import { LoginDetails } from 'src/app/models/login';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { ToasterService } from 'src/app/core/services/toastr.service';
import { TranslateService } from '@ngx-translate/core';
import { SocketService } from 'src/app/core/services/socket.service';
import { InventoryService } from 'src/app/core/services/inventory.service';
import { OutletService } from 'src/app/core/services/outlet.service';
import {
  AppLanguage,
  LanguageService,
  SUPPORTED_LANGUAGES
} from 'src/app/core/services/language.service';
interface LowStockItem {
  productId: string;
  variantId: string;
  factoryId: string;
  availableQuantity: number;
  productName?: string;
  vendorName?: string;
  threshold?: number;
}

interface LapsingProductResponse {
  statusCode: number;
  message: string;
  data: {
    vendors: Array<{
      _id: string;
      vendorName: string;
      products: Array<{
        productId: string;
        productName: string;
        variantId: string;
        factoryId: string;
        availableQuantity: number;
      }>;
    }>;
    totalVendors: number;
    page: number;
    limit: number;
  };
  status: number;
}
@Component({
  selector: 'mega-store-vendor-after-login',
  templateUrl: './after-login.component.html',
  styleUrls: ['./after-login.component.scss']
})
export class AfterLoginComponent implements OnInit, OnDestroy {
  // Language
  languages = SUPPORTED_LANGUAGES;
  currentLanguage: AppLanguage = 'en';
  assetsConst = assetsConst;
  sidenavWidth = 18;
  destroy$: Subject<boolean> = new Subject<boolean>();
  profileDetails: LoginDetails | null = null;
  subscriptionSubject$: Subscription;
  lowStockItems: LowStockItem[] = [];
  lowStockCount: number = 0;
  private readonly LOW_STOCK_THRESHOLD = 10; // You can adjust this threshold
  outlets: Array<{ _id: string; outletName: string }> = [];
  selectedOutletId: string | null = null;
  private vendorId: string = '';

  constructor(
    private _languageService: LanguageService,
    public _authService: AuthService,
    private _spinnerService: SpinnerService,
    private _toasterService: ToasterService,
    private _translateService: TranslateService,
    private router: Router,
    private socketService: SocketService,
    private _inventoryService: InventoryService,
    private _outletService: OutletService
  ) {
    this._languageService.language$
      .pipe(takeUntil(this.destroy$))
      .subscribe((language) => (this.currentLanguage = language));
    this.socketService.initSocket();
    this.vendorId = localStorage.getItem(environment.vendorIdKey) ?? '';
    this.subscriptionSubject$ = this._authService.profileDetails
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: LoginDetails | null) => {
        if (res) {
          this.profileDetails = res;
          this.outlets = res.assignedOutlets ?? [];
          if (!this.outlets.length) {
            this.loadHeaderOutlets();
          }
        } else {
          this.getProfileDetails();
        }
      });
  }

  changeLanguage(language: AppLanguage): void {
    this._languageService.switchTo(language);
  }

  ngOnInit(): void {
  }

  loadLowStockItems(): void {
    if (this.profileDetails) {
      this._inventoryService.getLapsingProducts(this.profileDetails._id).subscribe({
        next: (response: LapsingProductResponse) => {
          if (response.statusCode === 200 && response.data.vendors) {
            // Flatten and process the data
            const allProducts: LowStockItem[] = [];
            
            response.data.vendors.forEach(vendor => {
              vendor.products.forEach(product => {
                allProducts.push({
                  ...product,
                  vendorName: vendor.vendorName,
                  threshold: this.LOW_STOCK_THRESHOLD
                });
              });
            });
  
            // Sort by quantity and take top 10
            this.lowStockItems = allProducts
              .sort((a, b) => a.availableQuantity - b.availableQuantity)
              .slice(0, 10);
  
            // Update count
            this.lowStockCount = this.lowStockItems.length;
          }
        },
        error: (error: any) => {
          console.error('Error loading low stock items:', error);
        }
      });
    }
  }

  toggleSidenav(): void {
    this._authService.toggle();
  }

  getProfileDetails(): void {
    this._spinnerService.addToLoader('getProfile');
    this._authService
      .getProfile()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: LoginDetails) => {
          if (res) {
            this._spinnerService.removeFromLoader('getProfile');
            this.profileDetails = res;
            this._authService.profileDetails.next(res);
            this.loadLowStockItems();
            this.loadHeaderOutlets();
            // Refresh low stock items every 5 minutes
            // interval(5 * 60 * 1000).subscribe(() => {
            //   this.loadLowStockItems();
            // });
          }
        },
        error: () => {
          this._spinnerService.removeFromLoader('getProfile');
        }
      });
  }

  loadHeaderOutlets(): void {
    if (!this.vendorId) {
      this.vendorId = localStorage.getItem(environment.vendorIdKey) ?? '';
    }
    if (!this.vendorId) {
      return;
    }
    this._spinnerService.addToLoader('headerOutlets');
    this._outletService
      .getOutletList('', this.vendorId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this._spinnerService.removeFromLoader('headerOutlets');
          this.outlets = res?.outlets ?? [];
          this.initializeSelectedOutlet();
        },
        error: () => {
          this._spinnerService.removeFromLoader('headerOutlets');
        }
      });
  }

  onOutletChange(outletId: string): void {
    this.setSelectedOutlet(outletId);
    location.reload();
  }

  private initializeSelectedOutlet(): void {
    if (!this.outlets.length) {
      this.selectedOutletId = null;
      localStorage.removeItem(environment.selectedOutletKey);
      return;
    }
    const storedOutletId = localStorage.getItem(environment.selectedOutletKey);
    const validStoredOutlet = this.outlets.find(
      outlet => outlet._id === storedOutletId
    );
    const outletToSelect = validStoredOutlet
      ? validStoredOutlet._id
      : this.outlets[0]._id;
    this.setSelectedOutlet(outletToSelect);
  }

  private setSelectedOutlet(outletId: string): void {
    this.selectedOutletId = outletId;
    localStorage.setItem(environment.selectedOutletKey, outletId);
  }

  logout(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this._authService.logoutAlert().then((res: any) => {
      if (
        Object.prototype.hasOwnProperty.call(res, 'value') &&
        res.isConfirmed === true
      ) {
        this._spinnerService.addToLoader('logout');
        this._authService
          .logout()
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res: logoutResponse) => {
              if (res.statusCode === responseStatus.SUCCESS) {
                localStorage.removeItem(environment.storageKey);
                this._spinnerService.removeFromLoader('logout');
                this.router.navigate(['/auth/login']);
              }
            }
          });
      }
    });
  }

  onToggleChanged(event: MatSlideToggleChange): void {
    const body: { isAvailable: boolean } = {
      isAvailable: event.checked
    };
    this._spinnerService.addToLoader('changeStatusOfVendor');
    this._authService
      .updateProfile(body)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this._toasterService.successToastr(
            this._translateService.instant(
              event.checked ? 'vendorOnline' : 'vendorOffline'
            )
          );
          this._authService.profileDetails.next(res);
          this._spinnerService.removeFromLoader('changeStatusOfVendor');
        },
        error: () => {
          this._spinnerService.removeFromLoader('changeStatusOfVendor');
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.complete();
    this.destroy$.unsubscribe();
  }
}
